import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import {
  AddendumFormType,
  applyWhatNeedsUpdatingExclusivity,
  TextInput,
  WhatNeedsUpdating,
} from '@uplift/ui-v2';
import { Session } from '@uplift/api';
import { useProviderCptCodesQuery, useSessionCptCodesQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { NoteSection } from '../../pages/notes-page/components/NoteSection';
import { NoteSectionCloseButton } from '../../pages/notes-page/components/NoteSectionCloseButton';
import { AddonCptCodesField, ProviderPrimaryCptCodeAutocomplete } from '../PsychiatryNotes';

export type AmendAddendumCptSectionProps = {
  providerId: string;
  session: Session;
};

export const AmendAddendumCptSection: React.VFC<AmendAddendumCptSectionProps> = ({ providerId, session }) => {
  const currentUser = useCurrentuser();
  const formik = useFormikContext<AddendumFormType>();
  const [ , , whatNeedsUpdatingHelpers ] = useField<WhatNeedsUpdating[]>('whatNeedsUpdating');
  const { data: asSignedCptCodes } = useSessionCptCodesQuery(session);
  const { data: { cptCodes = [], filterOutRules = [] } = {} } = useProviderCptCodesQuery(providerId);

  useEffect(() => {
    const primaryCode = cptCodes.find(c => c.id === formik.values.primaryCptCodeId)?.code;
    const filterOutCodes = new Set(filterOutRules.filter(r => r.ifSelected === primaryCode).map(r => r.filterOut));

    if (!filterOutCodes.size) {
      return;
    }

    const keptAddonCptCodeIds = formik.values.addonCptCodeIds.filter(id => {
      const code = cptCodes.find(c => c.id === id)?.code;

      return !code || !filterOutCodes.has(code);
    });

    if (keptAddonCptCodeIds.length !== formik.values.addonCptCodeIds.length) {
      formik.setFieldValue('addonCptCodeIds', keptAddonCptCodeIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ formik.values.primaryCptCodeId, cptCodes, filterOutRules ]);

  const handleClose = () => {
    whatNeedsUpdatingHelpers.setValue(applyWhatNeedsUpdatingExclusivity(formik.values.whatNeedsUpdating, formik.values.whatNeedsUpdating.filter(v => v !== 'cpt')));
    formik.setFieldValue('primaryCptCodeId', session.cptCodeId ?? null);
    formik.setFieldValue('addonCptCodeIds', session.addonCptCodeIds ?? []);
    formik.setFieldValue('cptCodeChangeReason', '');
  };

  const closeIcon = Boolean(formik.values.cptCodeChangeReason)
    || formik.values.primaryCptCodeId !== (session.cptCodeId ?? null)
    || formik.values.addonCptCodeIds.length !== (session.addonCptCodeIds ?? []).length;

  return (
    <NoteSection
      title="Amend CPT Codes"
      onDelete={<NoteSectionCloseButton onDelete={handleClose} closeIcon={closeIcon} />}
    >
      <Box px={3}>
        <Box pb={3}>
          <Typography variant="overline" color="textSecondary">As signed</Typography>
          <Typography>
            {asSignedCptCodes?.length ? asSignedCptCodes.map(({ code, name }) => `${code}: ${name}`).join(', ') : 'None'}
          </Typography>
        </Box>
        <Box pb={3}>
          <TextInput
            name="cptCodeChangeReason"
            label="Code change reason"
            color="secondary"
            multiline
            rows={3}
            fullWidth
          />
        </Box>
        <ProviderPrimaryCptCodeAutocomplete
          label="Primary CPT code"
          name="primaryCptCodeId"
          providerId={providerId}
          medicalRole={currentUser.medicalRole}
        />
        <Box pt={2}>
          <AddonCptCodesField
            name="addonCptCodeIds"
            providerId={providerId}
            primaryCptCodeId={formik.values.primaryCptCodeId ?? undefined}
            buttonTextVariant
          />
        </Box>
      </Box>
    </NoteSection>
  );
};
